'use client';

import * as React from 'react';

import { useIsMobile } from '../hooks/use-mobile';
import { cn } from '../utils/cn';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './dialog';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from './drawer';

// Root component - conditionally renders Drawer or Dialog
type ResponsiveDialogProps = React.ComponentPropsWithoutRef<typeof Dialog> & {
  /**
   * Mobile drawer only: when true, only the grab handle can drag-dismiss.
   * Use for interactive content (e.g. image crop) that would otherwise scroll
   * the sheet while the user pans.
   */
  handleOnly?: boolean;
};

const ResponsiveDialog = ({
  children,
  handleOnly,
  ...props
}: ResponsiveDialogProps) => {
  const isMobile = useIsMobile();

  if (isMobile) {
    return (
      <Drawer handleOnly={handleOnly} {...props}>
        {children}
      </Drawer>
    );
  }

  return <Dialog {...props}>{children}</Dialog>;
};
ResponsiveDialog.displayName = 'ResponsiveDialog';

export interface ResponsiveDialogContentProps extends React.ComponentPropsWithoutRef<
  typeof DialogContent
> {
  /** When false, the drawer body does not scroll on mobile. Default true. */
  scrollable?: boolean;
  /** When true (default on mobile drawer), sheet height follows content. */
  fitContent?: boolean;
  /** Disable custom native-keyboard repositioning for stable full-screen flows. */
  keyboardAware?: boolean;
  /** Render the mobile drawer edge-to-edge at full viewport height. */
  fullscreenOnMobile?: boolean;
}

const ResponsiveDialogContent = React.forwardRef<
  React.ElementRef<typeof DialogContent>,
  ResponsiveDialogContentProps
>(
  (
    {
      className,
      children,
      scrollable = true,
      fitContent = true,
      keyboardAware = true,
      fullscreenOnMobile = false,
      ...props
    },
    ref,
  ) => {
    const isMobile = useIsMobile();

    if (isMobile) {
      return (
        <DrawerContent
          ref={ref}
          className={cn(
            fullscreenOnMobile &&
              'h-[calc(100dvh-env(safe-area-inset-top))]! max-h-[calc(100dvh-env(safe-area-inset-top))]! rounded-none! border-0!',
            className,
          )}
          scrollable={scrollable}
          fitContent={fullscreenOnMobile ? false : fitContent}
          keyboardAware={keyboardAware}
          {...props}
        >
          {children}
        </DrawerContent>
      );
    }

    return (
      <DialogContent ref={ref} className={className} {...props}>
        {children}
      </DialogContent>
    );
  },
);
ResponsiveDialogContent.displayName = 'ResponsiveDialogContent';

// Header component - conditionally renders DrawerHeader or DialogHeader
const ResponsiveDialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => {
  const isMobile = useIsMobile();

  if (isMobile) {
    // DrawerHeader automatically extends to edges with -mx-4
    return <DrawerHeader className={className} {...props} />;
  }

  return <DialogHeader className={className} {...props} />;
};
ResponsiveDialogHeader.displayName = 'ResponsiveDialogHeader';

// Title component - conditionally renders DrawerTitle or DialogTitle
const ResponsiveDialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogTitle>,
  React.ComponentPropsWithoutRef<typeof DialogTitle>
>(({ className, ...props }, ref) => {
  const isMobile = useIsMobile();

  if (isMobile) {
    return <DrawerTitle ref={ref} className={className} {...props} />;
  }

  return <DialogTitle ref={ref} className={className} {...props} />;
});
ResponsiveDialogTitle.displayName = 'ResponsiveDialogTitle';

// Description component - conditionally renders DrawerDescription or DialogDescription
const ResponsiveDialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogDescription>,
  React.ComponentPropsWithoutRef<typeof DialogDescription>
>(({ className, ...props }, ref) => {
  const isMobile = useIsMobile();

  if (isMobile) {
    return <DrawerDescription ref={ref} className={className} {...props} />;
  }

  return <DialogDescription ref={ref} className={className} {...props} />;
});
ResponsiveDialogDescription.displayName = 'ResponsiveDialogDescription';

// Footer component - conditionally renders DrawerFooter or DialogFooter
const ResponsiveDialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => {
  const isMobile = useIsMobile();

  if (isMobile) {
    // DrawerFooter automatically extends to edges with -mx-4
    return <DrawerFooter className={className} {...props} />;
  }

  return <DialogFooter className={className} {...props} />;
};
ResponsiveDialogFooter.displayName = 'ResponsiveDialogFooter';

export {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogDescription,
  ResponsiveDialogFooter,
  ResponsiveDialogHeader,
  ResponsiveDialogTitle,
};
